import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { trackEvent, setTrackedUser } from "@/lib/trackEvent";
import { gaPageView } from "@/lib/gtag";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import BlogGenerator from "./pages/BlogGenerator";
import ArticlePage from "./pages/ArticlePage";
import SectorPage from "./pages/SectorPage";
import PerformanceTracker from "./pages/PerformanceTracker";
import TestArticleGeneration from "./pages/TestArticleGeneration";
import WorkflowTest from "./pages/WorkflowTest";
import SimulationTest from "./pages/SimulationTest";
import ArticleArchive from "./pages/ArticleArchive";
import Opportunities from "./pages/Opportunities";
import Setup from "./pages/Setup";
import Auth from "./pages/Auth";
import Account from "./pages/Account";
import Watchlist from "./pages/Watchlist";
import MyReport from "./pages/MyReport";
import Portfolio from "./pages/Portfolio";
import NotFound from "./pages/NotFound";
import AutomationDashboard from "./pages/admin/AutomationDashboard";
import ArticlesList from "./pages/ArticlesList";
import ArticleDetail from "./pages/ArticleDetail";
import CronSetupPage from "./pages/admin/CronSetupPage";
import Analytics from "./pages/Analytics";
import SchedulerDashboard from "./pages/admin/SchedulerDashboard";
import TokenScoringDashboard from "./pages/TokenScoringDashboard";
import ProtocolRevenueDashboard from "./pages/ProtocolRevenueDashboard";
import ContentBootstrap from "./pages/admin/ContentBootstrap";
import ShareAnalytics from "./pages/admin/ShareAnalytics";
import ArticleView from "./pages/ArticleView";
import CoinReviewer from "./pages/CoinReviewer";
import DailyPickSettings from "./pages/admin/DailyPickSettings";
import BacktestDashboard from "./pages/BacktestDashboard";
import LivePortfolio from "./pages/LivePortfolio";
import DailyOpportunities from "./pages/DailyOpportunities";
import GemDebug from "./pages/admin/GemDebug";
import MacroDiagnostic from "./pages/admin/MacroDiagnostic";
import UndervaluedGems from "./pages/UndervaluedGems";
import UndervaluedGemsMethodology from "./pages/UndervaluedGemsMethodology";
import Macro from "./pages/Macro";
import Buddy from "./pages/Buddy";
import DailyPick from "./pages/DailyPick";
import Founder from "./pages/Founder";
import HowTossWorks from "./pages/HowTossWorks";
import TrackRecord from "./pages/TrackRecord";
import MarketAnalytics from "./pages/MarketAnalytics";
import Cycle from "./pages/Cycle";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Disclaimer from "./pages/Disclaimer";
import Contact from "./pages/Contact";
import { ChatWidget } from "@/components/chat/ChatWidget";
import { FloatingActionMenu } from "@/components/FloatingActionMenu";
import { SiteNav } from "@/components/SiteNav";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { ScrollToTop } from "@/components/ScrollToTop";


const queryClient = new QueryClient();

const RouteChangeTracker = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    setTrackedUser(user?.email ?? user?.id ?? null);
  }, [user]);

  useEffect(() => {
    gaPageView(location.pathname + location.search);
    trackEvent('page_view', { path: location.pathname });
  }, [location.pathname, location.search]);
  return null;
};


const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteChangeTracker />
          <ScrollToTop />
          <SiteNav />
          <DisclaimerBanner />
          <Routes>

            <Route path="/" element={<Index />} />
            <Route path="/buddy" element={<Buddy />} />
            <Route path="/daily-pick" element={<DailyPick />} />
            {/* Admin sign-in lives on an unlinked route — not referenced in nav or footer. */}
            <Route path="/admin-access" element={<Auth />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/account" element={<Account />} />
            <Route path="/watchlist" element={<Watchlist />} />
            <Route path="/my-report" element={<MyReport />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/coin-reviewer" element={<CoinReviewer />} />
            <Route path="/performance" element={<PerformanceTracker />} />
            <Route path="/test-articles" element={<TestArticleGeneration />} />
            <Route path="/test-workflow" element={
              <ProtectedRoute requireAdmin>
                <WorkflowTest />
              </ProtectedRoute>
            } />
            <Route path="/simulation" element={
              <ProtectedRoute requireAdmin>
                <SimulationTest />
              </ProtectedRoute>
            } />
            <Route path="/admin/automation" element={
              <ProtectedRoute requireAdmin>
                <AutomationDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/cron-setup" element={
              <ProtectedRoute requireAdmin>
                <CronSetupPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/scheduler" element={
              <ProtectedRoute requireAdmin>
                <SchedulerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute requireAdmin>
                <Analytics />
              </ProtectedRoute>
            } />
            <Route path="/admin/token-scoring" element={
              <ProtectedRoute requireAdmin>
                <TokenScoringDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/protocol-revenue" element={
              <ProtectedRoute requireAdmin>
                <ProtocolRevenueDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/bootstrap" element={
              <ProtectedRoute requireAdmin>
                <ContentBootstrap />
              </ProtectedRoute>
            } />
            <Route path="/admin/share-analytics" element={
              <ProtectedRoute requireAdmin>
                <ShareAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/admin/daily-pick-settings" element={
              <ProtectedRoute requireAdmin>
                <DailyPickSettings />
              </ProtectedRoute>
            } />
            <Route path="/admin/gem-debug" element={
              <ProtectedRoute requireAdmin>
                <GemDebug />
              </ProtectedRoute>
            } />
            <Route path="/admin/macro-diagnostic" element={
              <ProtectedRoute requireAdmin>
                <MacroDiagnostic />
              </ProtectedRoute>
            } />
            <Route path="/articles" element={<ArticleArchive />} />
            <Route path="/blog-generator" element={
              <ProtectedRoute requireAdmin>
                <BlogGenerator />
              </ProtectedRoute>
            } />
            <Route path="/post/:slug" element={<ArticlePage />} />
            <Route path="/sector/:sectorId" element={<SectorPage />} />
            <Route path="/research" element={<ArticlesList />} />
            <Route path="/articles/:slug" element={<ArticleDetail />} />
            <Route path="/article/:id" element={
              <ProtectedRoute requireAdmin>
                <ArticleView />
              </ProtectedRoute>
            } />
            <Route path="/backtest" element={<BacktestDashboard />} />
            <Route path="/live-portfolio" element={<LivePortfolio />} />
            <Route path="/daily-opportunities" element={<DailyOpportunities />} />
            <Route path="/undervalued-gems" element={<UndervaluedGems />} />
            <Route path="/undervalued-gems/methodology" element={<UndervaluedGemsMethodology />} />
            <Route path="/macro" element={<Macro />} />
            <Route path="/founder" element={<Founder />} />
            <Route path="/how-toss-works" element={<HowTossWorks />} />
            <Route path="/track-record" element={<TrackRecord />} />
            <Route path="/market-analytics" element={<MarketAnalytics />} />
            <Route path="/cycle" element={<Cycle />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/contact" element={<Contact />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          {/* Keeps the floating buttons from covering the last rows of page content */}
          <div aria-hidden className="h-16 md:h-20" />
          <ChatWidget />
          <FloatingActionMenu />

        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
